import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Code,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconChevronRight, IconCopy } from '@tabler/icons-react';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import ToolCallCard from 'src/secret-menu/conversation-debugger/ToolCallCard';
import {
  ConversationMessage,
  messagePreview,
  messageText,
  prettyJson,
} from 'src/secret-menu/conversation-debugger/types';

const ROLE_COLORS: Record<string, string> = {
  system: 'gray',
  user: 'blue',
  assistant: 'orange',
  tool: 'teal',
};

function ExpandedContent({ message }: { message: ConversationMessage }) {
  const text = messageText(message.content);

  if (message.role === 'system') {
    return (
      <Code
        block
        style={{ fontSize: 11, maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}
      >
        {text}
      </Code>
    );
  }

  if (message.role === 'tool') {
    return (
      <Code
        block
        style={{ fontSize: 11, maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}
      >
        {prettyJson(text)}
      </Code>
    );
  }

  if (!text) {
    return null;
  }

  return (
    <Box fz={13}>
      <MarkdownView value={text} />
    </Box>
  );
}

export default function MessageCard({
  message,
  index,
  expanded,
  onToggle,
}: {
  message: ConversationMessage;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const roleColor = ROLE_COLORS[message.role] ?? 'gray';
  const thinkingContent = message.reasoning_content ?? message.reasoningContent;
  const text = messageText(message.content);

  return (
    <Paper
      withBorder
      radius="md"
      style={{
        borderLeft: `3px solid var(--mantine-color-${roleColor}-4)`,
        overflow: 'hidden',
      }}
    >
      <Group gap="xs" px="sm" py={8} wrap="nowrap" style={{ cursor: 'pointer' }} onClick={onToggle}>
        <IconChevronRight
          size={14}
          style={{
            flexShrink: 0,
            transform: expanded ? 'rotate(90deg)' : undefined,
            transition: 'transform 150ms ease',
            color: 'var(--mantine-color-gray-6)',
          }}
        />
        <Badge size="sm" color={roleColor} variant="light">
          {message.role}
        </Badge>
        <Text size="textXs" c="dimmed">
          #{index}
        </Text>
        {message.role === 'tool' && message.name && (
          <Badge size="xs" color="teal" variant="outline" style={{ textTransform: 'none' }}>
            {message.name}
          </Badge>
        )}
        {!expanded && (
          <Text size="textXs" c="dimmed" truncate="end" style={{ flex: 1, minWidth: 0 }}>
            {messagePreview(message)}
          </Text>
        )}
        <Group gap={4} wrap="nowrap" ml="auto">
          {text && (
            <Text size="textXs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {text.length.toLocaleString()} chars
            </Text>
          )}
          {text && (
            <CopyButton value={text} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy content'} withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color={copied ? 'teal' : 'gray'}
                    onClick={e => {
                      e.stopPropagation();
                      copy();
                    }}
                  >
                    {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          )}
        </Group>
      </Group>

      {expanded && (
        <Stack gap="xs" px="sm" pb="sm">
          {thinkingContent && (
            <Accordion variant="contained">
              <Accordion.Item value="thinking" role="region">
                <Accordion.Control>
                  <Text size="textXs" c="violet.6">
                    Thinking ({thinkingContent.length.toLocaleString()} chars)
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Box fz={12} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {thinkingContent}
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
          <ExpandedContent message={message} />
          {message.tool_calls?.map(tc => (
            <ToolCallCard key={tc.id} toolCall={tc} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}
